import { useState } from 'react';
import { Building2 } from 'lucide-react';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Modal } from '@/components/Modal';
import { EmptyState, ErrorState, Skeleton } from '@/components/States';
import { useBrand, useBrands, useUpdateBrand } from '@/hooks/use-brands';
import { describeError } from '@/lib/describe-error';
import { Roles, type Session } from '@/auth/keycloak';
import type { BrandDetail } from '@/lib/schemas';
import {
  BrandSelector,
  Checkbox,
  ReadOnlyNotice,
  Row,
} from '@/pages/settings/settings-ui';
import {
  inputClass,
  isDomain,
  isWholeNumberInRange,
  textareaClass,
  useSelectedBrand,
} from '@/pages/settings/settings-shared';

/**
 * Brand configuration — the domain a campaign sends from, the footer it carries, the postal address
 * the law requires, and the circuit breaker's thresholds.
 *
 * ── THERE IS NO DOMAIN-AUTHENTICATION STATUS HERE ─────────────────────────────────
 *
 * There used to be, prominently, on the reasoning that a control letting somebody tick it would let
 * them send unsigned mail past a checklist that believed it was fine. The reasoning was right about the
 * risk and wrong about the premise: nothing in this application could ever set that flag, because SPF,
 * DKIM and branded links are configured in SendGrid out of band. So the panel reported a verification
 * that never ran, and the checklist blocked every send on it.
 *
 * What survives is the warning when the from-domain MOVES, which is a real consequence rather than an
 * inferred one: every sender address moves with it, and the new domain has to be authenticated in
 * SendGrid or the mail fails SPF and DKIM alignment. The form says so before the save, because that is
 * the point at which somebody can still decide not to.
 */
export function BrandsPage({ session }: { session: Session }) {
  const isAdministrator = session.roles.includes(Roles.Admin);

  const { data, isPending, isError, error, refetch } = useBrands();
  const [brandId, setBrandId] = useSelectedBrand((data?.items ?? []).map((brand) => brand.id));

  return (
    <div className="px-4 py-6 sm:px-6">
      <div>
        <h1 className="text-xl font-semibold text-heading">Brands</h1>
        <p className="mt-1 text-sm text-muted">
          The domain a campaign sends from, the footer it carries, and when the circuit breaker stops
          a send.
        </p>
      </div>

      {!isAdministrator && <ReadOnlyNotice />}

      {isPending ? (
        <div className="mt-5 flex flex-col gap-3">
          <Skeleton className="h-10" />
          <Skeleton className="h-64" />
        </div>
      ) : isError ? (
        <div className="mt-5">
          <ErrorState error={error} onRetry={() => void refetch()} />
        </div>
      ) : data.items.length === 0 ? (
        <div className="mt-5 rounded-lg border border-border bg-surface">
          <EmptyState
            icon={Building2}
            title="No brands yet"
            description="A brand is the domain a campaign sends from, the footer every message carries, and the postal address the law requires in it. Nothing can be sent until one exists."
          />
        </div>
      ) : (
        <>
          <BrandSelector brands={data.items} selectedId={brandId} onSelect={setBrandId} />

          {brandId !== undefined && (
            <BrandConfiguration brandId={brandId} isAdministrator={isAdministrator} />
          )}
        </>
      )}
    </div>
  );
}

function BrandConfiguration({
  brandId,
  isAdministrator,
}: {
  brandId: string;
  isAdministrator: boolean;
}) {
  const { data, isPending, isError, error, refetch } = useBrand(brandId);
  const [editing, setEditing] = useState(false);

  if (isPending) return <Skeleton className="mt-5 h-64" />;

  if (isError) {
    return (
      <div className="mt-5">
        <ErrorState error={error} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="mt-5 flex flex-col gap-5">
      <section className="overflow-hidden rounded-lg border border-border bg-surface">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold text-heading">Configuration</h2>

          {isAdministrator && (
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
              Edit configuration
            </Button>
          )}
        </div>

        <dl className="divide-y divide-border">
          <Row label="Marketing domain" value={data.fromDomain} />
          <Row
            label="System domain"
            value={data.systemFromDomain}
            hint="Confirmations and password-style mail. Separate on purpose: a marketing reputation problem must not stop double opt-in going out."
          />
          <Row label="Default from name" value={data.defaultFromName} />
          <Row label="Reply-to" value={data.replyToAddress ?? 'Not set'} />
          <Row label="IP pool" value={data.ipPoolName ?? 'Shared'} />
          <Row
            label="Postal address"
            value={data.postalAddress}
            hint="Required in every marketing email under PECR and CAN-SPAM."
          />
          <Row
            label="Approval required"
            value={data.requiresApproval ? 'Yes' : 'No'}
            hint={
              data.requiresApproval
                ? 'A campaign needs a named approver other than its author before it can send.'
                : 'Off. A mandatory workflow for a single-sender brand is friction people route around, which teaches them to route around it when it matters.'
            }
          />
          <Row
            label="Segmentation attributes"
            value={data.segmentationAttributes.join(', ') || 'None'}
            hint="What a template may branch on. Each one multiplies the potential variant count by its number of values, so this list stays short deliberately."
          />
          <Row
            label="Frequency cap"
            value={
              data.frequencyCap === null
                ? 'None'
                : `At most ${data.frequencyCap.maxSends} ` +
                  `${data.frequencyCap.maxSends === 1 ? 'message' : 'messages'} in any ` +
                  `${data.frequencyCap.windowDays} ${data.frequencyCap.windowDays === 1 ? 'day' : 'days'}`
            }
            hint={
              data.frequencyCap === null
                ? 'No cap. Every campaign goes to everyone the segment matches.'
                : 'A rolling window. Anyone already at the limit is held back and reported on the pre-send checklist, never dropped silently.'
            }
          />
          <Row
            label="Circuit breaker"
            value={`Hard bounce ${data.breakerThresholds.hardBouncePercent}% · Complaints ${data.breakerThresholds.complaintPercent}% · Blocks ${data.breakerThresholds.blockPercent}%`}
            hint={`Evaluated once ${data.breakerThresholds.minimumDelivered.toLocaleString()} messages have been delivered — below that, one bounce reads as a huge rate.`}
          />
        </dl>
      </section>

      {editing && <BrandForm brand={data} onClose={() => setEditing(false)} />}
    </div>
  );
}

/**
 * The brand form.
 *
 * The domain warning appears BEFORE saving, as soon as the field differs from what is stored. Telling
 * somebody afterwards that every sender address just moved is telling them too late to decide.
 */
function BrandForm({ brand, onClose }: { brand: BrandDetail; onClose: () => void }) {
  const [isCapped, setCapped] = useState(brand.frequencyCap !== null);
  const [capMaxSends, setCapMaxSends] = useState(String(brand.frequencyCap?.maxSends ?? 2));
  const [capWindowDays, setCapWindowDays] = useState(String(brand.frequencyCap?.windowDays ?? 7));

  // The same bounds the server enforces, so an administrator gets an answer without a round trip.
  const capError = {
    maxSends:
      isCapped && !isWholeNumberInRange(capMaxSends, 1, 1000)
        ? 'Between 1 and 1,000.'
        : undefined,
    windowDays:
      isCapped && !isWholeNumberInRange(capWindowDays, 1, 365)
        ? 'Between 1 and 365 days.'
        : undefined,
  };
  const [name, setName] = useState(brand.name);
  const [fromDomain, setFromDomain] = useState(brand.fromDomain);
  const [systemFromDomain, setSystemFromDomain] = useState(brand.systemFromDomain);
  const [defaultFromName, setDefaultFromName] = useState(brand.defaultFromName);
  const [replyToAddress, setReplyToAddress] = useState(brand.replyToAddress ?? '');
  const [ipPoolName, setIpPoolName] = useState(brand.ipPoolName ?? '');
  const [postalAddress, setPostalAddress] = useState(brand.postalAddress);
  const [footerHtml, setFooterHtml] = useState(brand.footerHtml);
  const [attributes, setAttributes] = useState(brand.segmentationAttributes.join(', '));
  const [requiresApproval, setRequiresApproval] = useState(brand.requiresApproval);
  const [isActive, setIsActive] = useState(brand.isActive);
  const [touched, setTouched] = useState(false);

  const save = useUpdateBrand(brand.id);

  const domainMoving = fromDomain.trim().toLowerCase() !== brand.fromDomain.toLowerCase();

  const errors = validateBrand({ name, fromDomain, systemFromDomain, defaultFromName, postalAddress });

  return (
    <Modal
      title={`Edit ${brand.name}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>

          <Button type="submit" form="brand-form" variant="primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <form
        id="brand-form"
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          setTouched(true);

          const firstBad = Object.keys(errors)[0];

          if (firstBad !== undefined) {
            document.getElementById(firstBad)?.focus();
            return;
          }

          save.mutate(
            {
              name: name.trim(),
              fromDomain: fromDomain.trim(),
              systemFromDomain: systemFromDomain.trim(),
              defaultFromName: defaultFromName.trim(),
              replyToAddress: replyToAddress.trim() === '' ? null : replyToAddress.trim(),
              ipPoolName: ipPoolName.trim() === '' ? null : ipPoolName.trim(),
              footerHtml,
              postalAddress: postalAddress.trim(),
              segmentationAttributes: attributes
                .split(',')
                .map((attribute) => attribute.trim())
                .filter((attribute) => attribute !== ''),
              breakerThresholds: brand.breakerThresholds,
              // Always sent, never omitted: absence cannot express "turn the cap off".
              frequencyCap: isCapped
                ? { maxSends: Number(capMaxSends), windowDays: Number(capWindowDays) }
                : null,
              requiresApproval,
              isActive,
            },
            { onSuccess: onClose },
          );
        }}
      >
        <Field id="name" label="Brand name" error={touched ? errors.name : undefined}>
          <input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
        </Field>

        <Field
          id="fromDomain"
          label="Marketing domain"
          error={touched ? errors.fromDomain : undefined}
          hint="The domain campaigns send from. Just the domain — no @, no https://."
        >
          <input
            id="fromDomain"
            type="text"
            value={fromDomain}
            onChange={(e) => setFromDomain(e.target.value)}
            className={inputClass}
          />
        </Field>

        {/* Before the save, not after — this is the last moment somebody can decide not to. */}
        {domainMoving && (
          <p role="alert" className="rounded-md bg-warning-bg px-3 py-2 text-sm text-warning-ink">
            Every sender address on this brand moves to <strong>{fromDomain.trim()}</strong>. Check that
            domain is authenticated in SendGrid first — if it is not, the mail will fail SPF and DKIM
            alignment and Gmail and Yahoo will reject or spam-folder it.
          </p>
        )}

        <Field
          id="systemFromDomain"
          label="System domain"
          error={touched ? errors.systemFromDomain : undefined}
          hint="Must differ from the marketing domain, so a marketing reputation problem cannot stop confirmations going out."
        >
          <input
            id="systemFromDomain"
            type="text"
            value={systemFromDomain}
            onChange={(e) => setSystemFromDomain(e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field id="defaultFromName" label="Default from name" error={touched ? errors.defaultFromName : undefined}>
          <input
            id="defaultFromName"
            type="text"
            value={defaultFromName}
            onChange={(e) => setDefaultFromName(e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field id="replyToAddress" label="Reply-to address" hint="Optional. Where replies go if a sender address does not set its own.">
          <input
            id="replyToAddress"
            type="email"
            value={replyToAddress}
            onChange={(e) => setReplyToAddress(e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field
          id="postalAddress"
          label="Postal address"
          error={touched ? errors.postalAddress : undefined}
          hint="Appears in every message. Required under PECR and CAN-SPAM, so this cannot be left blank."
        >
          <textarea
            id="postalAddress"
            rows={2}
            value={postalAddress}
            onChange={(e) => setPostalAddress(e.target.value)}
            className={textareaClass}
          />
        </Field>

        <Field
          id="footerHtml"
          label="Footer HTML"
          hint="Injected into every campaign, above the unsubscribe link."
        >
          <textarea
            id="footerHtml"
            rows={4}
            value={footerHtml}
            onChange={(e) => setFooterHtml(e.target.value)}
            className={`${textareaClass} font-mono text-xs`}
          />
        </Field>

        <Field
          id="attributes"
          label="Segmentation attributes"
          hint="Comma-separated. Each one a template may branch on — and each multiplies the potential variant count, so keep the list short."
        >
          <input
            id="attributes"
            type="text"
            value={attributes}
            onChange={(e) => setAttributes(e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field id="ipPoolName" label="IP pool" hint="Optional. Leave blank to use the shared pool.">
          <input
            id="ipPoolName"
            type="text"
            value={ipPoolName}
            onChange={(e) => setIpPoolName(e.target.value)}
            className={inputClass}
          />
        </Field>

        {/* The cap, behind a checkbox rather than as two always-visible numbers. Off is the default
            and the common case, and a pair of inputs showing "2 / 7" when no cap is in force reads as
            a cap that IS in force. */}
        <Checkbox
          id="isCapped"
          label="Cap how often one person is emailed"
          hint="Holds back anyone this brand has already mailed its maximum inside the window. They are reported on the pre-send checklist, never dropped silently."
          checked={isCapped}
          onChange={setCapped}
        />

        {isCapped && (
          <div className="grid gap-4 pl-7 sm:grid-cols-2">
            <Field
              id="capMaxSends"
              label="At most"
              hint="Messages per person."
              error={capError.maxSends}
            >
              <input
                id="capMaxSends"
                type="number"
                min={1}
                max={1000}
                value={capMaxSends}
                onChange={(event) => setCapMaxSends(event.target.value)}
                aria-invalid={capError.maxSends !== undefined}
                aria-describedby={capError.maxSends === undefined ? 'capMaxSends-hint' : 'capMaxSends-error'}
                className={inputClass}
              />
            </Field>

            <Field
              id="capWindowDays"
              label="In any"
              hint="Days. A rolling window, not a calendar week."
              error={capError.windowDays}
            >
              <input
                id="capWindowDays"
                type="number"
                min={1}
                max={365}
                value={capWindowDays}
                onChange={(event) => setCapWindowDays(event.target.value)}
                aria-invalid={capError.windowDays !== undefined}
                aria-describedby={capError.windowDays === undefined ? 'capWindowDays-hint' : 'capWindowDays-error'}
                className={inputClass}
              />
            </Field>
          </div>
        )}

        <Checkbox
          id="requiresApproval"
          label="Require approval before sending"
          hint="A named approver other than the author has to sign off. Off by default — a mandatory workflow for a single-sender brand is friction people route around."
          checked={requiresApproval}
          onChange={setRequiresApproval}
        />

        <Checkbox
          id="isActive"
          label="Brand is active"
          hint="An inactive brand cannot be used for new campaigns. Existing reports are unaffected."
          checked={isActive}
          onChange={setIsActive}
        />

        {save.isError && (
          <p role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-ink">
            {describeError(save.error).message}
          </p>
        )}
      </form>
    </Modal>
  );
}

/**
 * The sender form.
 *
 * The domain is shown as static text beside the input, not as an editable field: the address is always
 * on the brand's marketing domain, and a free-text address is one typo away from sending unsigned mail.

/**
 * The same rules the server enforces, duplicated so an administrator gets an answer without a round
 * trip. The server remains the authority and will refuse the same things.
 */
function validateBrand(input: {
  name: string;
  fromDomain: string;
  systemFromDomain: string;
  defaultFromName: string;
  postalAddress: string;
}): Record<string, string> {
  const errors: Record<string, string> = {};

  if (input.name.trim() === '') errors.name = 'Give the brand a name.';
  if (!isDomain(input.fromDomain)) errors.fromDomain = 'That is not a domain. Just the domain — no @, no https://.';
  if (!isDomain(input.systemFromDomain)) {
    errors.systemFromDomain = 'That is not a domain. Just the domain — no @, no https://.';
  } else if (input.systemFromDomain.trim().toLowerCase() === input.fromDomain.trim().toLowerCase()) {
    errors.systemFromDomain = 'The system domain must differ from the marketing domain.';
  }
  if (input.defaultFromName.trim() === '') errors.defaultFromName = 'Enter a from name.';
  if (input.postalAddress.trim() === '') {
    errors.postalAddress = 'A postal address is required in every marketing email.';
  }

  return errors;
}
